import React, { useState } from 'react';
import { Resizable } from 're-resizable';
import { Button, SpaceBetween } from '@cloudscape-design/components';
import { useCopilot } from './CopilotContext';
import { CopilotMessageList } from './CopilotMessageList';
import { CopilotMessageInput } from './CopilotMessageInput';
import { ASTRA_ICON_URL } from './brand';
import './Copilot.scss';

interface PanelSize {
  width: number;
  height: number;
}

// Minimum drag size (px); the max is capped in CSS (Copilot.scss).
const MIN_WIDTH = 320;
const MIN_HEIGHT = 360;

// Default/maximized sizes as viewport fractions with px caps (no design tokens exist for
// panel dimensions). re-resizable is size-controlled, so these seed its numeric size.
const DEFAULT_WIDTH_CAP = 480;
const DEFAULT_WIDTH_FRACTION = 0.35;
const DEFAULT_HEIGHT_FRACTION = 0.7;
const MAX_WIDTH_CAP = 920;
const MAX_WIDTH_FRACTION = 0.92;
const MAX_HEIGHT_FRACTION = 0.88;

// Mirrors the CSS ceiling in Copilot.scss (`max(360px, calc(95vh - 96px))`). Heights are
// clamped to this so a size-controlled height never exceeds — and gets clipped by — the CSS.
const BOTTOM_ANCHOR_FRACTION = 0.95;
const TOP_CHROME_PX = 96;

function viewportWidth(): number {
  return typeof window === 'undefined' ? 1024 : window.innerWidth;
}

function viewportHeight(): number {
  return typeof window === 'undefined' ? 768 : window.innerHeight;
}

/** Tallest height the CSS ceiling allows; matches `max-height` in Copilot.scss. */
function maxAvailableHeight(): number {
  return Math.max(
    MIN_HEIGHT,
    Math.round(viewportHeight() * BOTTOM_ANCHOR_FRACTION) - TOP_CHROME_PX,
  );
}

/** Default normal-mode size, seeded from the viewport. */
function defaultNormalSize(): PanelSize {
  return {
    width: Math.min(
      DEFAULT_WIDTH_CAP,
      Math.round(viewportWidth() * DEFAULT_WIDTH_FRACTION),
    ),
    height: Math.min(
      Math.round(viewportHeight() * DEFAULT_HEIGHT_FRACTION),
      maxAvailableHeight(),
    ),
  };
}

/** Fixed (not full-screen) size used by the maximize control. */
function maximizedSize(): PanelSize {
  return {
    width: Math.min(MAX_WIDTH_CAP, Math.round(viewportWidth() * MAX_WIDTH_FRACTION)),
    height: Math.min(
      Math.round(viewportHeight() * MAX_HEIGHT_FRACTION),
      maxAvailableHeight(),
    ),
  };
}

export function CopilotPanel() {
  const { state, startNewConversation, togglePanel } = useCopilot();
  // sizeMode = expanded size; collapsed = minimized to the header. Kept separate so
  // restoring returns to the previous size.
  const [sizeMode, setSizeMode] = useState<'normal' | 'maximized'>('normal');
  const [collapsed, setCollapsed] = useState(false);
  const [normalSize, setNormalSize] = useState<PanelSize>(defaultNormalSize);

  if (!state.isOpen) return null;

  const isMinimized = collapsed;
  const isMaximized = sizeMode === 'maximized';
  const resizeEnabled = !isMaximized && !isMinimized;

  // Minimized collapses to a fixed default-width header bar (height auto); otherwise use
  // the maximized or the user's drag size. normalSize is preserved for restore.
  const currentSize: { width: number | string; height: number | string } = isMinimized
    ? { width: defaultNormalSize().width, height: 'auto' }
    : isMaximized
      ? maximizedSize()
      : normalSize;

  const panelClassName = [
    'copilot-panel',
    isMaximized && !collapsed ? 'copilot-panel--maximized' : '',
    isMinimized ? 'copilot-panel--minimized' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const toggleMaximize = () => {
    setSizeMode((m) => (m === 'maximized' ? 'normal' : 'maximized'));
    setCollapsed(false);
  };
  const toggleMinimize = () => setCollapsed((c) => !c);

  return (
    <Resizable
      className={panelClassName}
      // Override re-resizable's default position:relative; anchor + max-size live in the SCSS.
      style={{ position: 'fixed' }}
      size={currentSize}
      minWidth={resizeEnabled ? MIN_WIDTH : undefined}
      minHeight={resizeEnabled ? MIN_HEIGHT : undefined}
      // No bounds="window": it assumes a top-left anchor and would clamp our bottom-right
      // panel's growth; CSS max-width/height cap size instead. Resize from the top/left
      // edges and top-left corner — the edges away from the anchor.
      enable={resizeEnabled ? { top: true, left: true, topLeft: true } : false}
      handleClasses={{
        top: 'copilot-resize-handle',
        left: 'copilot-resize-handle',
        topLeft: 'copilot-resize-handle',
      }}
      handleComponent={{
        topLeft: (
          <span
            className="copilot-resize-grip"
            role="separator"
            aria-label="Resize Astra"
          />
        ),
      }}
      onResizeStop={(_event, _direction, ref) => {
        setSizeMode('normal');
        setCollapsed(false);
        setNormalSize({ width: ref.offsetWidth, height: ref.offsetHeight });
      }}
    >
      {/* Visible card; holds the dialog role and styling (re-resizable doesn't type DOM props). */}
      <div
        className="copilot-panel-surface"
        role="dialog"
        aria-label="Astra Audit Assistant"
      >
        {/* awsui-dark-mode scopes Cloudscape's dark tokens to this bar so it matches the
            app's dark TopNavigation; brand text/icons pick up on-dark colors automatically. */}
        <div className="copilot-panel-header awsui-dark-mode">
          <div className="copilot-brand">
            <span className="copilot-brand-icon" aria-hidden="true">
              <img className="copilot-brand-icon-img" src={ASTRA_ICON_URL} alt="" />
            </span>
            <span className="copilot-brand-text">
              <span className="copilot-brand-title">Astra</span>
              <span className="copilot-brand-subtitle">Audit Assistant</span>
            </span>
          </div>
          <SpaceBetween direction="horizontal" size="xxs">
            <Button
              variant="icon"
              iconName="refresh"
              onClick={startNewConversation}
              ariaLabel="New conversation"
            />
            <Button
              variant="icon"
              iconName={isMinimized ? 'angle-up' : 'angle-down'}
              onClick={toggleMinimize}
              ariaLabel={isMinimized ? 'Restore Astra' : 'Minimize Astra'}
            />
            <Button
              variant="icon"
              iconName={isMaximized ? 'shrink' : 'expand'}
              onClick={toggleMaximize}
              ariaLabel={isMaximized ? 'Restore Astra size' : 'Maximize Astra'}
            />
            <Button
              variant="icon"
              iconName="close"
              onClick={togglePanel}
              ariaLabel="Close Astra"
            />
          </SpaceBetween>
        </div>

        {!isMinimized && (
          <>
            <div className="copilot-panel-messages">
              <CopilotMessageList />
            </div>

            <div className="copilot-panel-input">
              <CopilotMessageInput />
            </div>
          </>
        )}
      </div>
    </Resizable>
  );
}
